/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { router, discovery, envUtil, auth2 as auth } from "dc-core";
import {
  orderedContextMenuVerbIds,
  contextMenuPlacements,
  modernViewerDropinContext,
  multiDocContextMenuVerbList,
  multiDocDisabledContextMenuVerbList,
  skipAnalyticsOnExecuteActionVerbs
} from "constants/enums";
import { CONTEXT_MENU_SELECTION_NAME } from "constants/constants";
import {
  getContextMenuXPosition,
  getContextMenuTriggerType,
  getContextMenuTriggerPageIndex,
  getContextMenuTriggerBBox,
  getContextMenuTriggerZoomLevel,
  getContextMenuDefaultPlacement,
  getContextMenuTriggerEventObj,
  getDisabledContextMenuVerbs
} from "selectors/contextMenuSelectors";
import { getPageHandle, isValidPageIndex } from "selectors/pageSelectors";
import { isAllowed } from "utils/DocPermissionsUtils";
import { COPY_TEXT_PERM } from "constants/constants";
import { getPagesViewBoundingRect } from "selectors/documentViewSelectors";
import { getTextSelectionPageRange, isTouchSelectionPresent } from "dc-preview-utils/src/selectors/touchSelectors";
import { getCanvasQuadsUnion } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { scaleRectangle, translateRectangle, rectsIntersection } from "utils/common";
import { allSettled } from "utils/PromiseUtils";
import { CONTEXT_MENU_MARGIN, CONTEXT_MENU_POSITION_DELTA, CONTEXT_MENU_MARGIN_COMMENTING } from "constants/constants";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { getPageZoomLevel } from "selectors/zoom";
import {
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer,
  getShowPremiumIconForNonDCWebClients,
  getIsMultiDocModeEnabled
} from "selectors/appSelectors";
import { providers } from "dc-core";
import { contextMenuManager } from "contextMenuManager";
import { getLatestHitTestResponse } from "../selectors/documentSelectors";
import { pageContentTypes, pageContentTypesReverseMap } from "../pageContentHandler/HitTester";
import { VERB_COPY_LINK, VERB_COPY_TEXT, VERB_OPEN_LINK } from "../constants/enums";
import {
  getShouldRenderPersonalCommentsDropin,
  getShouldRenderCommentsDropin,
  getIsDemoMode
} from "selectors/overlayDropinSelectors";
import { trackContextMenuVerb } from "../analytics/events";
import { triggerContextMenuItemClicked } from "../publicApis/Events/events";
import { VERB_TO_VERB_EVENT_NAME } from "../constants/enums";
import { getFeatureFlagApis } from "../publicApis/featureFlag";
import { UPSELL_VERBS_FOR_ANONYMOUS_USER, EDIT_TEXT_RECIPIENT_FUNNEL } from "../constants/constants";
import { enableToolsInMultidoc } from "./FeatureFlagUtils";

const callDropinMethod = (dropinId, methodName, opts) =>
  typeof dropinId === "string"
    ? discovery.callBootstrapMethod(dropinId, methodName, opts)
    : dropinId[methodName] && dropinId[methodName](opts);

export const fetchVerbIcon = (verbId, opts = {}) => callDropinMethod(verbId, "getIcon", opts);
export const fetchVerbLabel = (verbId, opts = {}) => callDropinMethod(verbId, "getLabel", opts);
// Pass callback to close the context menu once an option in custom UI component is selected or some other scenario
export const fetchVerbMenuItemCustomUI = (verbId, opts = {}, stores) =>
  callDropinMethod(verbId, "getMenuItemCustomUI", {
    ...opts,
    closeContextMenu: () => contextMenuManager.clearMenuItems(stores)
  });
let UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST;

const getUpsellVerbs = async () => {
  if (UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST === undefined) {
    try {
      const { getFeatureFlag } = getFeatureFlagApis();
      UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST = (await getFeatureFlag(EDIT_TEXT_RECIPIENT_FUNNEL))
        ? [orderedContextMenuVerbIds.VERB_EDIT_PDF_VIEWER]
        : [];
      UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST = UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST.concat(
        await getFeatureFlag(UPSELL_VERBS_FOR_ANONYMOUS_USER)
      );
    } catch (error) {
      UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST = [];
    }
  }
  return UPSELL_VERBS_FOR_ANONYMOUS_USER_LIST;
};

const getContextMenuEvents = async verbId => {
  const dropin = await discovery.loadDropin(verbId);
  const { bootstrap } = dropin;
  if (bootstrap.getContextMenuEvents) {
    return bootstrap.getContextMenuEvents();
  }
};

export const callGetUpsellParams = async (verbId, opts = {}) => {
  const UPSELL_VERBS_FOR_ANONYMOUS = await getUpsellVerbs();
  if (
    (envUtil.getEnvVar("app_identifier") === "dc-web-app" ||
      envUtil.getEnvVar("app_identifier") === "dc-web-pwa" ||
      opts.showPremiumIconForNonDCWebClients) &&
    ((auth && auth.isSignedIn) || UPSELL_VERBS_FOR_ANONYMOUS.includes(verbId))
  ) {
    const dropin = await discovery.loadDropin(verbId);
    const { bootstrap } = dropin;
    if (bootstrap.getUpsellParams) {
      return bootstrap.getUpsellParams(opts);
    }
  }
  return undefined;
};
export const callExecuteAction = async (verbId, opts = {}) => {
  triggerContextMenuItemClicked(verbId);
  await callDropinMethod(verbId, "executeAction", opts);
};
export const callInitializeAction = (verbId, opts = {}) => callDropinMethod(verbId, "initializeAction", opts);
export const getPlacement = (verbId, opts = {}) => callDropinMethod(verbId, "getPlacement", opts);
export const getTooltip = (verbId, opts = {}) => callDropinMethod(verbId, "getTooltip", opts);
export const getPremiumIconTooltip = (verbId, opts = {}) => callDropinMethod(verbId, "getPremiumIconTooltip", opts);
export const shouldStayOpenOnSelect = (verbId, opts = {}) => callDropinMethod(verbId, "shouldStayOpenOnSelect", opts);
export const getIsSection = (verbId, opts = {}) => callDropinMethod(verbId, "isSection", opts);
export const getChildren = (verbId, opts = {}) => callDropinMethod(verbId, "getChildren", opts);
export const getSecondaryHeaderComponent = (verbId, opts = {}) =>
  callDropinMethod(verbId, "getSecondaryHeaderComponent", opts);
export const getBadge = (verbId, opts = {}) => callDropinMethod(verbId, "getBadge", opts);

export const callExecutionActionAndTrackEvents = async (verbId, context, opts = {}) => {
  await callExecuteAction(verbId, { ...opts, context });
  if (skipAnalyticsOnExecuteActionVerbs.some(verb => verb.verbId === verbId && verb.triggerType === opts.triggerType)) {
    return;
  }
  trackContextMenuVerb(VERB_TO_VERB_EVENT_NAME[verbId] || verbId, opts.triggerType);
};

const validateMenuItem = item => {
  const isInvalid =
    !item.title ||
    (item.isSection && item.children.length === 0) ||
    (item.placement === contextMenuPlacements.CONTEXT_MENU_HEADER && !item.icon);
  return !isInvalid;
};

const getSectionConfig = verbConfig => {
  const secondaryHeaderComponent = verbConfig.value[9].value;
  const verbId = verbConfig.value[0].value;
  const sectionId = typeof verbId === "string" ? verbId : "";
  return { secondaryHeaderComponent, sectionId };
};

const getMenuItemFromConfig = async (stores, verbConfig, verbContext) => {
  const isDCWebApp =
    envUtil.getEnvVar("app_identifier") === "dc-web-app" || envUtil.getEnvVar("app_identifier") === "dc-web-pwa";
  const upsellParams = verbConfig.value[6].value;
  const shouldShowPremiumIcon = upsellParams && upsellParams.isPremium;
  const children = verbConfig.value[8].value;
  const isSectionVerb = verbConfig.value[7].value;
  const verbId = verbConfig.value[0].value;
  let childVerbsConfig = [],
    sectionConfig = {};

  if (children && children.length) {
    childVerbsConfig = await getMenuItems(stores, children, verbContext);
  }
  if (isSectionVerb) {
    sectionConfig = getSectionConfig(verbConfig);
  }
  const menuItem = {
    verbId: typeof verbId === "string" ? verbId : "",
    icon: verbConfig.value[1].value,
    title: verbConfig.value[2].value,
    eventHandler: async (opts = {}) => {
      opts.isMenuItemCustomUI = !!verbConfig.value[11].value;
      if (typeof verbId !== "string") {
        await callExecuteAction(verbId, { ...opts, context: verbContext });
        return;
      }
      const toolItem = { bootstrap: { id: verbId } };
      const context = "mv-contextMenu";

      if (isDCWebApp && upsellParams && upsellParams.shouldUpsell) {
        providers.intent().then(intentProvider => {
          intentProvider.setLastVerbInfo(toolItem, "clicked", context);
        });
      }
      opts.triggerType = getContextMenuTriggerType(stores);
      opts.demoMode = getIsDemoMode(stores);
      await callExecutionActionAndTrackEvents(verbId, verbContext, opts);
    },
    placement: verbConfig.value[3].value || contextMenuPlacements.CONTEXT_MENU_BODY,
    stayOpenOnSelect: verbConfig.value[4].value || false,
    tooltip: verbConfig.value[5].value,
    shouldShowPremiumIcon,
    isSection: isSectionVerb,
    children: childVerbsConfig,
    badge: verbConfig.value[10] && verbConfig.value[10].value,
    menuItemCustomUI: verbConfig.value[11].value,
    mouseEvents: verbConfig.value[12].value,
    premiumIconTooltip: verbConfig.value[13].value,
    ...sectionConfig
  };
  return menuItem;
};

const getMenuItems = async (stores, verbs, verbContext) => {
  // get verbs configs
  const triggerType = getContextMenuTriggerType(stores);
  const promiseForAll = verbs.map(item => {
    callInitializeAction(item, {
      selectionName: CONTEXT_MENU_SELECTION_NAME,
      preserveSelection: true,
      triggerEventObj: getContextMenuTriggerEventObj(stores),
      pageIndex: getContextMenuTriggerPageIndex(stores),
      triggerContentType: getContextMenuTriggerContentType(stores),
      previewConfig: {
        shouldRenderPersonalCommentsDropin: getShouldRenderPersonalCommentsDropin(stores),
        shouldRenderCommentsDropin: getShouldRenderCommentsDropin(stores)
      },
      context: verbContext,
      triggerType
    });

    const icon = fetchVerbIcon(item, { context: verbContext, triggerType });
    const label = fetchVerbLabel(item, { context: verbContext, triggerType });
    const menuItemCustomUI = fetchVerbMenuItemCustomUI(item, { context: verbContext, triggerType }, stores);
    const placement = getPlacement(item, {
      triggerEventObj: getContextMenuTriggerEventObj(stores),
      context: verbContext,
      triggerType
    });
    const tooltip = getTooltip(item, {
      triggerEventObj: getContextMenuTriggerEventObj(stores),
      context: verbContext,
      triggerType
    });
    const premiumIconTooltip = getPremiumIconTooltip(item, { context: verbContext, triggerType });
    const stayOpenOnSelect = shouldStayOpenOnSelect(item, { context: verbContext, triggerType });
    const upsellParams = callGetUpsellParams(item, {
      showPremiumIconForNonDCWebClients: getShowPremiumIconForNonDCWebClients(stores),
      triggerType
    });
    const mouseEvents = getContextMenuEvents(item);
    const isSection = getIsSection(item, { context: verbContext, triggerType });
    const children = getChildren(item, { context: verbContext, triggerType });
    const secondaryHeaderComponent = getSecondaryHeaderComponent(item, { context: verbContext, triggerType });
    const badge = getBadge(item, { context: verbContext, triggerType });

    const propsList = [
      item,
      icon,
      label,
      placement,
      stayOpenOnSelect,
      tooltip,
      upsellParams,
      isSection,
      children,
      secondaryHeaderComponent,
      badge,
      menuItemCustomUI,
      mouseEvents,
      premiumIconTooltip
    ];
    if (Promise && !Promise.allSettled) {
      return allSettled(propsList);
    }
    return Promise.allSettled(propsList);
  });
  let verbsConfig;

  if (Promise && !Promise.allSettled) {
    verbsConfig = await allSettled(promiseForAll);
  } else {
    verbsConfig = await Promise.allSettled(promiseForAll);
  }

  const promisesForMenuItems = verbsConfig.map(verbConfig => getMenuItemFromConfig(stores, verbConfig, verbContext));

  const menuItems = await Promise.all(promisesForMenuItems);
  const validMenuItems = menuItems.filter(item => validateMenuItem(item));
  return validMenuItems;
};

// menuItems are either verbIds or object that implement the verb interface
export const sortAndGetMenuItems = async (menuItems, stores) => {
  let verbContext = getShouldUseModernViewer(stores) ? modernViewerDropinContext.CONTEXT_MENU : "ViewerContextMenu";
  if (getShouldUseMobileModernViewer(stores)) {
    verbContext = modernViewerDropinContext.MODERN_MOBILE_CONTEXT_MENU;
  }

  const disabledVerbs = getDisabledContextMenuVerbs(stores);
  const verbItems = menuItems.filter(verbId => typeof verbId === "string" && !disabledVerbs.includes(verbId));
  const objItems = menuItems.filter(item => typeof item !== "string");

  const orderedVerbs = Object.values(orderedContextMenuVerbIds);
  const sortedVerbIds = verbItems.sort((a, b) => orderedVerbs.indexOf(a) - orderedVerbs.indexOf(b));
  let sortedMenuItems = await getMenuItems(stores, [...sortedVerbIds, ...objItems], verbContext);
  if (getIsMultiDocModeEnabled(stores)) {
    const enableTools = await enableToolsInMultidoc();
    sortedMenuItems = sortedMenuItems.filter(({ verbId }) =>
      enableTools ? !multiDocDisabledContextMenuVerbList.includes(verbId) : multiDocContextMenuVerbList.includes(verbId)
    );
  }
  return sortedMenuItems;
};

export const getContextMenuTriggerContentType = stores => {
  const triggerType = getContextMenuTriggerType(stores);

  if (triggerType === contextMenuTriggerType.TEXT_SELECTION) return pageContentTypesReverseMap[pageContentTypes.TEXT];
  else if (triggerType === contextMenuTriggerType.IMAGE_SELECTION)
    return pageContentTypesReverseMap[pageContentTypes.IMAGE];
  else if (triggerType !== contextMenuTriggerType.RIGHT_CLICK) return pageContentTypesReverseMap[pageContentTypes.NONE];

  const latestHitTestResult = getLatestHitTestResponse(stores);

  if (
    latestHitTestResult.contentType === pageContentTypes.TEXT ||
    latestHitTestResult.contentType === pageContentTypes.HYPERLINK
  )
    return pageContentTypesReverseMap[pageContentTypes.TEXT];
  else if (latestHitTestResult.contentType === pageContentTypes.IMAGE)
    return pageContentTypesReverseMap[pageContentTypes.IMAGE];

  return pageContentTypesReverseMap[pageContentTypes.NONE];
};

const getSelectionBox = stores => {
  let selectionBox;
  const index = getTextSelectionPageRange(stores).startPage;
  if (isValidPageIndex(stores, index)) {
    const pageRect = getPageHandle(stores, index).getBoundingClientRect();
    if (pageRect.bottom - pageRect.top <= 0 || pageRect.right - pageRect.left <= 0) return undefined;
    selectionBox = getCanvasQuadsUnion(stores, index);
    if (!selectionBox) return undefined;
    const pageZoomLevel = getPageZoomLevel(stores, index);
    selectionBox = scaleRectangle(selectionBox, pageZoomLevel);
    selectionBox = translateRectangle(selectionBox, pageRect.left, pageRect.top);
  }
  return selectionBox;
};

const getTriggerBBox = stores => {
  if (
    getContextMenuTriggerType(stores) === contextMenuTriggerType.IMAGE_SELECTION ||
    getContextMenuTriggerType(stores) === contextMenuTriggerType.TEXT_SELECTION ||
    getContextMenuTriggerType(stores) === contextMenuTriggerType.RIGHT_CLICK
  ) {
    return getSelectionBox(stores);
  }

  const index = getContextMenuTriggerPageIndex(stores);
  const pageRect = getPageHandle(stores, index).getBoundingClientRect();
  if (pageRect.bottom - pageRect.top <= 0 || pageRect.right - pageRect.left <= 0) return undefined;
  let triggerBBox = getContextMenuTriggerBBox(stores);
  if (!triggerBBox) return undefined;
  const pageZoomLevel = getPageZoomLevel(stores, index) / getContextMenuTriggerZoomLevel(stores);
  triggerBBox = scaleRectangle(triggerBBox, pageZoomLevel);
  if (getContextMenuTriggerType(stores) !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK) {
    triggerBBox = translateRectangle(triggerBBox, pageRect.left, pageRect.top);
  }
  if (triggerBBox.top > triggerBBox.bottom) {
    // swap the values
    triggerBBox.top += triggerBBox.bottom;
    triggerBBox.bottom = triggerBBox.top - triggerBBox.bottom;
    triggerBBox.top -= triggerBBox.bottom;
  }
  if (triggerBBox.left > triggerBBox.right) {
    // swap the values
    triggerBBox.left += triggerBBox.right;
    triggerBBox.right = triggerBBox.left - triggerBBox.right;
    triggerBBox.left -= triggerBBox.right;
  }
  return triggerBBox;
};

const isSelectionInView = stores => {
  const pagesViewBox = getPagesViewBoundingRect(stores);
  let triggerBBox = getTriggerBBox(stores);
  if (triggerBBox === undefined) return false;
  let intersectionRect = rectsIntersection(pagesViewBox, triggerBBox);
  let width = intersectionRect.right - intersectionRect.left;
  let height = intersectionRect.bottom - intersectionRect.top;
  return width > 0 && height > 0;
};

const getPositionInTouchDevices = (stores, handle, menuItems) => {
  const useModernViewer = getShouldUseModernViewer(stores);
  const triggerType = getContextMenuTriggerType(stores);
  if (isSelectionInView(stores)) {
    const pagesViewBox = getPagesViewBoundingRect(stores);
    let triggerBBox = getTriggerBBox(stores);
    if (triggerBBox === undefined) return {};
    let top, left;

    let totalHeight = 0;
    let totalWidth = 0;
    const condition = useModernViewer
      ? triggerType !== contextMenuTriggerType.RIGHT_CLICK &&
        triggerType !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK &&
        triggerType !== contextMenuTriggerType.TEXT_SELECTION &&
        triggerType !== contextMenuTriggerType.IMAGE_SELECTION &&
        triggerType !== contextMenuTriggerType.COMMENT_SELECTED &&
        triggerType !== contextMenuTriggerType.EDIT_TEXT &&
        triggerType !== contextMenuTriggerType.EDIT_IMAGE &&
        triggerType !== contextMenuTriggerType.IMAGE_CLICK
      : triggerType !== contextMenuTriggerType.RIGHT_CLICK &&
        triggerType !== contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK;
    if (condition) {
      let additionalWidth = 0; // for the side ends
      for (let i = 0; i < menuItems.length; i++) {
        totalWidth += menuItems[i].width;
        totalHeight = Math.max(totalHeight, menuItems[i].height);
        // not adding separator for now
        // if (i > 0 && menuItems[i - 1].position !== menuItems[i].position) {
        //   additionalWidth += 9; // separator width (1px) with margin (8px)
        // }
      }
      if (
        (!useModernViewer && triggerType === contextMenuTriggerType.TEXT_SELECTION) ||
        triggerType === contextMenuTriggerType.COMMENT_SELECTED
      ) {
        additionalWidth += menuItems.length * 8; // these values come CSS, when modifying CSS, update values here as well. (extra padding of 8px around items)
      }
      totalWidth += additionalWidth;
    } else {
      for (let i = 0; i < handle.children.length; i++) {
        totalHeight += handle.children[i].getBoundingClientRect().height;
        totalWidth = Math.max(totalWidth, handle.children[i].getBoundingClientRect().width);
      }
    }

    let contextMenuMargin = CONTEXT_MENU_MARGIN;
    if (triggerType !== contextMenuTriggerType.RIGHT_CLICK && !isTouchSelectionPresent(stores))
      contextMenuMargin = CONTEXT_MENU_MARGIN_COMMENTING;

    if (triggerType === contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK) {
      contextMenuMargin = 0;
    }

    let aboveSpace = triggerBBox.top - pagesViewBox.top - contextMenuMargin;
    let belowSpace = pagesViewBox.bottom - (triggerBBox.bottom + contextMenuMargin);
    let leftSpace = triggerBBox.left - pagesViewBox.left - contextMenuMargin;
    let rightSpace = pagesViewBox.right - (triggerBBox.right + contextMenuMargin);

    const placement = getContextMenuDefaultPlacement(stores);
    const showAbove =
      (placement === "up" && aboveSpace > totalHeight) ||
      (placement === "down" && belowSpace <= totalHeight && aboveSpace > totalHeight);
    const showBelow =
      (placement === "up" && aboveSpace <= totalHeight && belowSpace > totalHeight) ||
      (placement === "down" && belowSpace > totalHeight);

    const showLeft = leftSpace > totalWidth;
    const showRight = rightSpace > totalWidth;
    let showLeftOrRight = false;

    /**
     * when bbox is selected, provided placement as 'topRight',
     * the order-priority for displaying ContextMenu is TopRight>Bottom>Top>TopLeft outside BBox
     * & if there is no space around bbox then contextMenu should be inside bbox on TopRight corner
     */
    if (placement === "topRight") {
      let noSpaceAroundBBox = false;
      let isTopOfBBoxVisible = true;
      if (showRight) {
        showLeftOrRight = true;
        left = triggerBBox.right + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
      } else if (belowSpace > totalHeight) {
        // show below if space availiable
        top = triggerBBox.bottom + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
      } else if (aboveSpace > totalHeight) {
        // show above
        top = triggerBBox.top - contextMenuMargin - totalHeight - CONTEXT_MENU_POSITION_DELTA;
      } else if (showLeft) {
        showLeftOrRight = true;
        left = triggerBBox.left - contextMenuMargin - totalWidth - CONTEXT_MENU_POSITION_DELTA;
      } else {
        noSpaceAroundBBox = true;
        isTopOfBBoxVisible = aboveSpace + contextMenuMargin > 0;
      }

      if (showLeftOrRight) {
        top = Math.min(
          Math.max(triggerBBox.top, pagesViewBox.top),
          pagesViewBox.bottom - totalHeight - contextMenuMargin
        );
      } else {
        left = Math.min(
          Math.max(Math.floor(triggerBBox.left / 2 + triggerBBox.right / 2 - totalWidth / 2), contextMenuMargin),
          pagesViewBox.right - totalWidth - contextMenuMargin
        );
      }
      /**
       * when there is no space for contextMenu around the BBox to render
       * then is should appear on the top-right corner inside the BBox
       * and if BBox top area is not visible then it should appear at the center
       */
      if (noSpaceAroundBBox) {
        if (isTopOfBBoxVisible) {
          top = triggerBBox.top + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
          left = triggerBBox.right - totalWidth - contextMenuMargin;
        } else {
          top = (triggerBBox.top + triggerBBox.bottom) / 2 + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
          left = (triggerBBox.left + triggerBBox.right) / 2 + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
        }
      }
    } else {
      /**
       * when either placement is not provided or provided as "up" or "down",
       * the order-priority for displaying ContextMenu is Bottom>Top>Left>Right
       */
      if (showBelow) {
        top = triggerBBox.bottom + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
      } else if (showAbove) {
        top = triggerBBox.top - contextMenuMargin - totalHeight - CONTEXT_MENU_POSITION_DELTA;
      } else if (showLeft) {
        showLeftOrRight = true;
        left = triggerBBox.left - contextMenuMargin - totalWidth - CONTEXT_MENU_POSITION_DELTA;
      } else if (showRight) {
        showLeftOrRight = true;
        left = triggerBBox.right + contextMenuMargin - CONTEXT_MENU_POSITION_DELTA;
      } else {
        top = triggerBBox.top / 2 + triggerBBox.bottom / 2;
      }

      // center of selection
      if (showLeftOrRight) {
        top = Math.min(
          Math.max(Math.floor(triggerBBox.top / 2 + triggerBBox.bottom / 2 - totalHeight / 2), contextMenuMargin),
          pagesViewBox.bottom - totalHeight - contextMenuMargin
        );
      } else {
        left = Math.min(
          Math.max(Math.floor(triggerBBox.left / 2 + triggerBBox.right / 2 - totalWidth / 2), contextMenuMargin),
          pagesViewBox.right - totalWidth - contextMenuMargin
        );
      }
    }
    if (triggerType === contextMenuTriggerType.HUD_OVERFLOW_BUTTON_CLICK) {
      if (getShouldUseModernViewer(stores)) {
        left = triggerBBox.right;
        if (left - totalWidth >= 0) {
          left -= totalWidth; // as context menu alignment is towards left
        }
      } else {
        left = getContextMenuXPosition(stores);
        if (left + totalWidth > window.innerWidth) {
          left -= totalWidth;
        }
      }
    }

    return { left, top };
  }
  return {};
};

export const initializeModernViewerContextMenu = fileType => {
  contextMenuManager.subscribe({
    name: "PreviewDropin",
    contextMenuHandler: () => (isAllowed(COPY_TEXT_PERM) ? [VERB_COPY_TEXT] : []),
    type: contextMenuTriggerType.TEXT_SELECTION
  });

  contextMenuManager.subscribe({
    name: "PreviewDropin",
    contextMenuHandler: () => [VERB_COPY_LINK, VERB_OPEN_LINK],
    type: contextMenuTriggerType.HYPERLINK_CLICK
  });
};

export const showEditInContextMenu = () => {
  const filePathsWithAllowedEditing = ["/link/file", "/link/file/"];
  const pathName = router.getLocation().pathname;
  const fileType = decodeURIComponent(getQueryParameterFromUrl(window.location.href, "filetype"));
  return (
    filePathsWithAllowedEditing.findIndex(allowedPath => allowedPath === pathName) !== -1 &&
    fileType === "application/pdf"
  );
};

export { getPositionInTouchDevices };
